<div
    aria-modal="true"
    class="backdrop"
    data-backdrop="true"
    role="dialog"
    tabindex="-1"
    (click)="onBackdrop($event)"
    (keydown.escape)="closed.emit()">
    <article class="dialog">
        <header class="head">
            <div class="head-text">
                <p class="kicker">{{ card().title }} · {{ sectionLabel() }}</p>
                <h2>{{ item().subject ?? item().text }}</h2>
                <p class="meta">{{ item().meta }}</p>
            </div>
            <button aria-label="Close" class="close" type="button" (click)="closed.emit()">✕</button>
        </header>

        @if (item().safety_note) {
            <p class="safety">⚠ {{ item().safety_note }}</p>
        }

        @if (item().thread_text) {
            <section class="block">
                <h3>Prior thread</h3>
                <pre class="body thread">{{ item().thread_text }}</pre>
            </section>
        }

        @if (item().body_text) {
            <section class="block">
                <h3>Latest message</h3>
                <pre class="body">{{ item().body_text }}</pre>
            </section>
        }

        @if (item().attachments && item().attachments!.length > 0) {
            <section class="block">
                <h3>Attachments ({{ item().attachments!.length }})</h3>
                <ul class="attachments">
                    @for (a of item().attachments; track a.id ?? a.name) {
                        <li>
                            <span class="att-name">{{ a.name }}</span>
                            @if (a.size) {
                                <span class="att-size">{{ a.size }} bytes</span>
                            }
                            @if (a.content_type) {
                                <span class="att-type">{{ a.content_type }}</span>
                            }
                        </li>
                    }
                </ul>
                <p class="hint">Open in Outlook to download.</p>
            </section>
        }

        @if (item().why_you || item().recommendation) {
            <section class="block why-block">
                @if (item().why_you) {
                    <p class="why-you">{{ item().why_you }}</p>
                }
                @if (item().recommendation) {
                    <p class="recommendation">Recommended: {{ item().recommendation }}</p>
                }
            </section>
        }

        @if (item().draft_reply !== undefined) {
            <section class="block">
                <h3>
                    {{ isSent() ? 'Sent reply' : 'Drafted reply' }}
                    @if (!isSent() && dirty()) {
                        <span class="dirty-flag">edited</span>
                    }
                </h3>
                <div class="reply-envelope" data-testid="reply-envelope">
                    <p class="env-line">
                        <span class="env-label">To</span><span class="env-val">{{ replyTo() }}</span>
                    </p>
                    @if (replyCc()) {
                        <p class="env-line" data-testid="review-cc">
                            <span class="env-label">Cc</span><span class="env-val">{{ replyCc() }}</span>
                        </p>
                    }
                    @if (hasSubject()) {
                        <p class="env-line">
                            <span class="env-label">Subject</span><span class="env-val">{{ replySubject() }}</span>
                        </p>
                    }
                </div>
                @if (isSent()) {
                    <!-- Read-only receipt: an already-sent message can't be edited or re-sent. -->
                    <pre class="body">{{ editedDraft() }}</pre>
                    <p class="hint">Already sent - this is a record of what went out.</p>
                } @else {
                    <textarea
                        class="draft-edit"
                        placeholder="Draft is empty - write a reply here."
                        rows="8"
                        [ngModel]="editedDraft()"
                        (ngModelChange)="editedDraft.set($event)"></textarea>
                    <p class="hint">Edits go out when you click Send. Dismiss suppresses without sending.</p>
                }
            </section>
        }

        @if (approvalActions()) {
            @if (scopeChoosing()) {
                <div class="scope-picker" data-testid="dialog-scope-chooser">
                    <app-scope-chooser
                        confirmLabel="Approve Always"
                        [rungs]="rungs()"
                        (cancelled)="scopeChoosing.set(false)"
                        (confirmed)="onScopeConfirm($event)" />
                </div>
            } @else {
                <footer class="actions" data-testid="dialog-approval-actions">
                    <button class="action" data-testid="dialog-reject" type="button" (click)="reject.emit()">Reject</button>
                    <button class="action primary" data-testid="dialog-approve" type="button" (click)="approve.emit()">Approve</button>
                    <button class="action" data-testid="dialog-approve-always" type="button" (click)="scopeChoosing.set(true)">
                        Approve Always
                    </button>
                </footer>
            }
        } @else if (!isSent() && item().actions && item().actions!.length > 0) {
            <footer class="actions">
                @for (act of item().actions; track act.kind) {
                    <button
                        class="action"
                        type="button"
                        [class.danger]="act.style === 'danger'"
                        [class.firing]="isFiring(act)"
                        [class.primary]="act.style === 'primary' || act.primary"
                        [class.resolved]="isResolved(act)"
                        [disabled]="isFiring(act) || isResolved(act)"
                        (click)="onAction(act, $event)">
                        {{ sendLabel(act) }}
                    </button>
                }
                @if (item().message_id) {
                    <button
                        class="action correct-btn"
                        type="button"
                        [disabled]="correcting() || correctionDone()"
                        (click)="openCorrectionPanel()">
                        @if (correctionDone()) {
                            ✓ feedback saved
                        } @else {
                            Correct {{ personaName() }}
                        }
                    </button>
                }
            </footer>
        }

        @if (correcting() && !correctionDone()) {
            <section class="correction-panel">
                <h3>What was wrong?</h3>
                <div class="correction-kinds">
                    @for (k of correctionKinds; track k.value) {
                        <label class="kind-pill" [class.selected]="correctionKind() === k.value">
                            <input
                                name="correctionKind"
                                type="radio"
                                [checked]="correctionKind() === k.value"
                                [value]="k.value"
                                (change)="correctionKind.set(k.value)" />
                            <span>{{ k.label }}</span>
                        </label>
                    }
                </div>
                <textarea
                    class="correction-note"
                    placeholder="Optional: what should I do differently next time?"
                    rows="3"
                    [ngModel]="correctionNote()"
                    (ngModelChange)="correctionNote.set($event)"></textarea>
                <div class="correction-actions">
                    <button class="action" type="button" [disabled]="correctionSubmitting()" (click)="closeCorrectionPanel()">
                        Cancel
                    </button>
                    <button class="action primary" type="button" [disabled]="correctionSubmitting()" (click)="submitCorrection()">
                        @if (correctionSubmitting()) {
                            Saving…
                        } @else {
                            Save feedback
                        }
                    </button>
                </div>
                <p class="hint">Saves as user-memory {{ personaName() }} pulls on future drafts. Original message is also dismissed.</p>
            </section>
        }
    </article>
</div>
